import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {ObjectModel, ObjectType} from '../../../shared/models/object.model';
import {PillLetter} from '../../../shared/models/pill';
import {BaseTableViewComponent} from '../_base-table-view/base-table-view.component';
import {SortTable, SortTableRow} from '../../../shared/models/sort-table/types';
import {SortTableCellKebabMenu, SortTableCellUserLink, SortTableRolePicker} from '../../../shared/models/sort-table/cell-classes';
import {SessionService} from '../../../core/services/session.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {UsersService} from '../../../core/services/users.service';
import {DrawerGapFromTopComponent} from '../drawer-gap-from-top/drawer-gap-from-top.component';
import {PillComponent} from '../pill/pill.component';
import {BannerComponent} from '../banner/banner.component';
import {UserLinkComponent} from '../user-link/user-link.component';
import {SortTableComponent} from '../sort-table/sort-table.component';
import {PermissionsService} from 'src/core/services/permissions.service';
import {Role} from 'src/shared/models/fga/role';
import {User} from '../../../shared/models/user';
import {forkJoin, map, switchMap, of, catchError} from 'rxjs';
import { extractErrorMessage } from 'src/shared/utils/error-utils';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';

const COL_IDX_KEBAB = 0;
const COL_IDX_GRANTEE = 1;
const COL_IDX_ROLE_PICKER = 2;

export interface UserGrant {
  user: User;
  roles: Role[];
}


@Component({
  selector: 'motn-share-manager',
  templateUrl: './share-manager.component.html',
  styleUrls: ['./share-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerGapFromTopComponent,
    PillComponent,
    BannerComponent,
    UserLinkComponent,
    SortTableComponent,
  ],
})
export class ShareManagerComponent extends BaseTableViewComponent implements OnInit {
  @Input() object: ObjectModel;

  pillLetter: PillLetter;

  userIsOwner: boolean = false;

  @Output() closed$: EventEmitter<null> = new EventEmitter<null>();
  rolePickerGrantees: UserGrant[];


  constructor(
    private permissionsService: PermissionsService,
    private shadeTools: ShadeToolsService,
    private session: SessionService,
    private userService: UsersService,
  ) {
    super();
  }


  ngOnInit(): void {
    switch (this.object.type) {
      case 'app':
        this.pillLetter = 'a';
        break;
      case ObjectType.DEVICE:
        this.pillLetter = 'd';
        break;
      case ObjectType.HUB:
        this.pillLetter = 'h';
        break;
      case ObjectType.TILE:
        this.pillLetter = 't';
        break;
    }

    this.fetchGrants();

    this.shadeTools.onUserSharingDialogClosed$.subscribe(_ => this.fetchGrants());

    this.userIsOwner = this.object.owner === this.session.userId;
  }


  private fetchGrants(): void {
    this.permissionsService.queryObjectGrantees(this.object.id, this.object.type).pipe(
      switchMap(grantees => forkJoin(
        grantees
        .filter(grantee => grantee.Type  === ObjectType.USER)
        .map(grantee =>
          this.userService.fetchUser$(grantee.ID).pipe(
            map(user => ({
              user,
              roles: grantee.Roles,
            })),
            catchError(err => {
              console.warn(`Failed to fetch user ${grantee.ID}:`, err);
              return of(null); // Continue processing other users
            }),
          ),
        ),
      )),
    ).subscribe({
      next: (results) => {
        // Do not show the owner in the sharing table because the owner already has all permissions.
        this.rolePickerGrantees = results.filter(grant => grant && !grant.roles?.includes(Role.OWNER));
        this.updateTable();
      },
      error: (err) => {
        this.shadeTools.errorDialog(extractErrorMessage(err, 'Failed to fetch grants. Please try again.'));
      },
    });
  }


  share() {
    this.shadeTools.openUserSharingDialog(this.object);
  }


  updateTable() {
    if (!this.rolePickerGrantees) {
      return;
    }

    const colKebabWidth = 40;
    const colGranteeWidth = 130;
    const colRolePickerWidth = 160;

    const table: SortTable = {
      css: {
        'min-width': (colKebabWidth + colGranteeWidth + colRolePickerWidth) + 'px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 50,
        startOnPage: 1,
        maxPageOptions: 10,
      },
    };

    {
      table.columns[COL_IDX_KEBAB] = {
        name: '',
        css: {
          'width': colKebabWidth + 'px',
          'justify-content': 'center',
        },
      };

      const grantxxWidth = `calc((100% - ${colKebabWidth + colRolePickerWidth}px) * 0.5)`;

      table.columns[COL_IDX_GRANTEE] = {
        name: 'Grantee',
        infoDialogText: `The grantee is the user with whom the ${this.object.type} was shared.`,
        css: {
          'width': grantxxWidth,
          'justify-content': 'start',
        },
        sortingFn: SortTableCellUserLink.sortingFnUsername,
      };

      const rolePickerWidth = `calc(35% + ${colKebabWidth}px)`;

      table.columns[COL_IDX_ROLE_PICKER] = {
        name: 'Roles',
        infoDialogText: `Each user listed may be granted one or more roles that impact what they can do with this ${this.object.type}.`,
        css: {
          'width': rolePickerWidth,
          'justify-content': 'start',
          // @ts-ignore
          'margin-left': '15%',
        },
      };
    }
    const rows: SortTableRow[] = [];

    for (let grant of this.rolePickerGrantees) {
      const userId = grant.user.ID;
      let row: SortTableRow = this.tableRows[userId];

      if (!row) {
        row = this.tableRows[userId] = {
          cells: [],
          context: userId.toString(),
        };
      }

      this.upsertRowData(row, grant);
      rows.push(row);
    }

    rows.sort((a, b) => {
      return a.context.localeCompare(b.context);
    });

    table.rows = rows;
    this.table$.next(table);
  }


  upsertRowData(row: SortTableRow, {user, roles}: UserGrant) {
    row.cells[COL_IDX_KEBAB] = new SortTableCellKebabMenu(user.ID, [
      {
        id: 'del',
        name: 'Remove User',
        callback: () => {
          // Check if user is removing themselves
          const isRemovingSelf = user.ID === this.session.userId;

          if (isRemovingSelf) {
            // Show confirmation dialog for removing self
            this.shadeTools.startDialog(
              '<span class="red"><i class="far fa-exclamation-triangle"></i> Remove Yourself?</span>',
              'By removing yourself as a user of this object, you may completely lose access to it. Are you sure?',
              [
                {
                  text: 'Cancel',
                  callback: () => {},
                  color: DialogBoxButtonColor.Default,
                },
                {
                  text: 'Remove',
                  callback: () => {
                    this.performUserRemoval(user.ID);
                  },
                  color: DialogBoxButtonColor.Red,
                }
              ],
              false,
              false,
              false,
              true
            );
          } else {
            // Not removing self - proceed without confirmation
            this.performUserRemoval(user.ID);
          }
        },
      },
    ], '', false, 1);
    row.cells[COL_IDX_GRANTEE] = new SortTableCellUserLink({userId: user.ID, user});
    row.cells[COL_IDX_ROLE_PICKER] = new SortTableRolePicker(user.ID, this.object, roles);
  }

  private performUserRemoval(userId: string) {
    const isRemovingSelf = userId === this.session.userId;

    this.permissionsService.setUserRoles({
      UserID: userId,
      ObjectID: this.object.id,
      ObjectType: this.object.type,
      Roles: [],
    }).subscribe(
      () => {
        this.rolePickerGrantees = this.rolePickerGrantees.filter(g => g.user.ID !== userId);
        this.updateTable();

        // If user removed themselves, close the share manager
        if (isRemovingSelf) {
          this.closed$.emit();
        }
      },
      (err) => {
        this.shadeTools.errorDialog(extractErrorMessage(err, 'Failed to remove user. Please try again.'));
      },
    );
  }
}
